import { useState, useEffect, useRef } from "react"
import { useQueryState } from "nuqs"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faStar, faStarHalfAlt } from "@fortawesome/free-solid-svg-icons"
import Dropdown from "../../components/Dropdown"
import DebouncedInput from "../../components/DebouncedInput"
import { useAllCustomerFeedbacks } from "../../hooks/useCustomerFeedback"
import ApiError from "../../components/ApiError"
import Loader from "../../components/Loader"
import { getDateRange } from "../../utils/datetime"

const CustomerFeedback = () => {
  const [searchTerm, setSearchTerm] = useState("")
  const [dateRange, setDateRange] = useQueryState("dateRange", {
    defaultValue: "This Month",
  })
  const [overallExperience, setOverallExperience] = useQueryState(
    "overallExperience",
    {
      defaultValue: "All",
    }
  )
  const [reportClarity, setReportClarity] = useQueryState("reportClarity", {
    defaultValue: "All",
  })

  const sentinelRef = useRef(null)

  const getDateFilterValue = (range) => {
    if (range === "All") return ""
    const interval = getDateRange(range)
    return [interval.start.toISODate(), interval.end.toISODate()].join(",")
  }

  const renderStarRating = (rating) => {
    const stars = []
    for (let i = 1; i <= 5; i++) {
      if (i <= rating) {
        stars.push(
          <FontAwesomeIcon
            key={i}
            className="text-yellow-400 w-4 h-4"
            icon={faStar}
          />
        )
      } else if (i - 0.5 <= rating) {
        stars.push(
          <FontAwesomeIcon
            key={i}
            className="text-yellow-400 w-4 h-4"
            icon={faStarHalfAlt}
          />
        )
      } else {
        stars.push(
          <FontAwesomeIcon
            key={i}
            className="text-gray-300 w-4 h-4"
            icon={faStar}
          />
        )
      }
    }

    return <div className="flex items-center gap-1">{stars}</div>
  }

  const {
    data: { data, total } = { data: [], total: 0 },
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useAllCustomerFeedbacks({
    limit: 20,
    sortBy: "CreatedAt:DESC",
    filters: {
      AccessionNo: searchTerm?.trim() || "",
      CreatedAt: getDateFilterValue(dateRange),
      OverallExperience:
        overallExperience === "All" ? undefined : parseInt(overallExperience),
      ReportClarity:
        reportClarity === "All" ? undefined : parseInt(reportClarity),
    },
  })

  // Infinite scroll with IntersectionObserver
  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      {
        rootMargin: "200px",
        threshold: 0.1,
      }
    )

    observer.observe(sentinel)

    return () => {
      observer.disconnect()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const dateRangeOptions = [
    "Today",
    "Yesterday",
    "This Week",
    "Last Week",
    "This Month",
    "Last Month",
    "All",
  ]
  const ratingOptions = ["All", "1", "2", "3", "4", "5"]

  const handleClearFilters = () => {
    setDateRange("This Month")
    setOverallExperience("All")
    setReportClarity("All")
  }

  if (isError) {
    return (
      <div className="mt-4">
        <ApiError axiosError={error} />
      </div>
    )
  }

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-2">Customer Feedback</h1>

      {/* Filter Row */}
      <div className="bg-gray-50 p-4 rounded-lg mb-2">
        <div className="flex gap-4">
          {/*Accession No.*/}
          <div>
            <label className="block text-xs font-medium mb-1 ml-1 text-gray-700">
              Accession No.
            </label>
            <DebouncedInput
              className="w-full min-w-[300px] px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Search Accession No."
              value={searchTerm}
              onChange={setSearchTerm}
            />
          </div>
          {/* Date Range */}
          <div>
            <Dropdown
              label="DATE RANGE"
              options={dateRangeOptions}
              setValue={setDateRange}
              title="Filter result according to date range"
              value={dateRange}
            />
          </div>
          {/* Overall Experience */}
          <div>
            <Dropdown
              label="OVERALL EXPERIENCE"
              options={ratingOptions}
              setValue={setOverallExperience}
              title="Filter by overall experience rating"
              value={overallExperience}
            />
          </div>
          {/* Report Clarity */}
          <div>
            <Dropdown
              label="REPORT CLARITY"
              options={ratingOptions}
              setValue={setReportClarity}
              title="Filter by report clarity rating"
              value={reportClarity}
            />
          </div>
          <div className="flex items-end justify-end">
            <button
              className="px-6 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm font-medium disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
              title="Clear all filters"
              onClick={handleClearFilters}
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="mt-2">
        {isLoading ? (
          <div className="flex items-center justify-center py-20 gap-4">
            <Loader />
            <span className="text-gray-600">Loading feedback...</span>
          </div>
        ) : (
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs text-gray-500">
                Showing <span className="font-bold">{data.length}</span> of{" "}
                <span className="font-bold">{total}</span> feedback entries
              </span>
            </div>
            {data && data.length > 0 ? (
              <div className="space-y-2 max-h-[calc(100vh-325px)] overflow-auto">
                {data.map((feedback) => (
                  <div
                    key={feedback.id}
                    className="bg-white border border-gray-200 rounded-lg hover:shadow-sm transition-shadow"
                  >
                    {/* Main Row */}
                    <div className="flex items-start justify-between p-3">
                      <div className="flex items-start gap-4">
                        {/* Accession Number */}
                        <span className="font-semibold text-gray-900 min-w-[180px] mt-1">
                          {feedback.AccessionNo || "N/A"}
                        </span>

                        {/* Overall Experience Rating */}
                        <div className="min-w-[140px]">
                          <div className="text-xs text-gray-500 mb-1">
                            Overall Experience
                          </div>
                          {renderStarRating(feedback.OverallExperience)}
                        </div>

                        {/* Report Clarity Rating */}
                        <div className="min-w-[140px]">
                          <div className="text-xs text-gray-500 mb-1">
                            Report Clarity
                          </div>
                          {renderStarRating(feedback.ReportClarity)}
                        </div>

                        {/* Comments Preview */}
                        <div className="flex-1 max-w-[900px]">
                          {feedback.Comments ? (
                            <span className="text-sm text-gray-600">
                              {feedback.Comments}
                            </span>
                          ) : (
                            <span className="text-sm text-gray-400 italic">
                              No comments
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Date with Dropdown */}
                      <div className="flex items-center gap-2 text-gray-500">
                        <span className="text-xs">
                          {new Date(feedback.CreatedAt).toLocaleDateString(
                            "en-US",
                            {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            }
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Infinite Scroll Sentinel */}
                <div ref={sentinelRef} className="h-1" />

                {/* Load More Section - Fallback */}
                {hasNextPage && (
                  <div className="flex justify-center py-4">
                    {isFetchingNextPage ? (
                      <div className="flex items-center gap-2">
                        <Loader />
                        <span className="text-gray-600">
                          Loading more feedback...
                        </span>
                      </div>
                    ) : (
                      <button
                        className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md transition-colors"
                        disabled={isFetchingNextPage}
                        onClick={fetchNextPage}
                      >
                        Load More Feedback
                      </button>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No feedback found. Try adjusting your filters.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default CustomerFeedback
